/**
 * RUBYTILL'S OWN INVOICE, for the operator verifying a payment.
 *
 * WHY NOT STRIPE'S PDF. Two independent reasons, both checked against a real
 * invoice rather than assumed:
 *
 *   1. `invoice_pdf` serves `x-frame-options: SAMEORIGIN` and a CSP
 *      `frame-ancestors` list we are not on — an iframe is refused outright.
 *   2. Its redirect target answers `Content-Disposition: attachment` with
 *      `application/octet-stream`, so even if it loaded it would download
 *      rather than render.
 *
 * WHY OURS SAYS MORE ANYWAY. Since §13/B1 the platform hands Stripe ONE managed
 * line per location at a net amount and tells it nothing about the plan, the
 * packages or the discounts. Stripe's invoice therefore CANNOT itemise what was
 * sold — by design. This one can, and that is the question the operator is
 * actually asking: what did we charge, and for what.
 *
 * WHY HTML AND A PRINT WINDOW, NOT A PDF FILE. super-admin-ui has no `jspdf`
 * (nor `jsbarcode`) and the working rules forbid adding packages — the same
 * constraint `unitLabelPrint.ts` documents and solves the same way. The OS
 * print dialog gives BOTH things asked for: print to a printer, or "Save as
 * PDF" to download. Same hand-off point a `doc.save()` would have reached.
 */

export interface InvoiceCharge {
  id: string;
  number?: string;
  status: string;
  /** Integer cents, as Stripe reports them. */
  amount: number;
  amountPaid: number;
  currency: string;
  periodStart?: number;
  periodEnd?: number;
  /**
   * Stripe's own reason — `subscription_create`, `subscription_cycle`,
   * `manual`. The ONLY reliable way to tell a subscription invoice from a
   * one-time one: Stripe stamps a billing PERIOD on both, so reading
   * `periodStart` labelled the deposits invoice "Monthly subscription".
   */
  billingReason?: string | null;
  /** The subscription that raised it, or null for a one-time invoice. */
  subscription?: string | null;
}

export interface TenantInvoiceInput {
  tenantName: string;
  tenantId: string;
  orderId?: string;
  /** What Stripe actually took. The authoritative figures on this document. */
  charges: InvoiceCharge[];
  /** The recurring commitment, integer cents. */
  monthlyCents?: number;
  /**
   * The monthly SPLIT. "Subscription, monthly" on its own says the money
   * recurs and nothing about what for — which is the question this document
   * exists to answer.
   */
  softwareMonthlyCents?: number;
  hardwareMonthlyCents?: number;
  /** Package names the tenant is billing on. */
  packageNames: string[];
  /** Financed hardware, month by month. */
  schedule: Array<{
    name: string;
    saleMode?: string;
    termMonths?: number;
    monthlyAmountCents: number;
  }>;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  /** Test-mode objects live under a different dashboard path. */
  stripeDashboardBase: string;
}

const money = (cents?: number | null): string =>
  typeof cents === 'number' && Number.isFinite(cents)
    ? `$${(cents / 100).toFixed(2)}`
    : '—';

const day = (ms?: number): string =>
  typeof ms === 'number' && ms > 0 ? new Date(ms).toLocaleDateString() : '—';

/**
 * Everything interpolated below is tenant- or Stripe-supplied, and it is being
 * written into a document. Escaped without exception.
 */
const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * NAME THE CHARGE, DON'T RECITE STRIPE'S COUNTER. "XPAVMYGE-0002" is Stripe's
 * own sequence and says nothing about what the money was for. Whether Stripe
 * recorded a BILLING PERIOD is the one fact that separates these: a
 * subscription invoice covers a period, the checkout charge does not.
 */
const chargeLabel = (c: InvoiceCharge): string => {
  if (c.periodStart) {
    const from = day(c.periodStart);
    const to = day(c.periodEnd);
    return to && to !== '—' ? `Monthly subscription · ${from} – ${to}` : 'Monthly subscription';
  }
  return 'Paid at checkout · hardware and deposits';
};

export function buildTenantInvoiceHtml(input: TenantInvoiceInput): string {
  const paidCents = input.charges
    .filter((c) => c.status === 'paid')
    .reduce((n, c) => n + (c.amountPaid || 0), 0);

  const chargeRows = input.charges.length
    ? input.charges.map((c) => `
      <tr>
        <td>
          <div class="strong">${esc(chargeLabel(c))}</div>
          <div class="muted mono">${esc(c.number || c.id)}</div>
        </td>
        <td>${esc(day(c.periodStart))}</td>
        <td><span class="pill ${c.status === 'paid' ? 'ok' : 'warn'}">${esc(c.status)}</span></td>
        <td class="right strong">${esc(money(c.amountPaid || c.amount))}</td>
      </tr>`).join('')
    : `<tr><td colspan="4" class="muted">No Stripe charges recorded against this account.</td></tr>`;

  const packageRows = input.packageNames.length
    ? input.packageNames.map((n) => `<li>${esc(n)}</li>`).join('')
    : `<li class="muted">No packages recorded on the active plan.</li>`;

  const scheduleRows = input.schedule.length
    ? input.schedule.map((s) => `
      <tr>
        <td>
          <div class="strong mono">${esc(s.name)}</div>
          <div class="muted">${esc(s.saleMode || 'financed')}${s.termMonths ? ` · ${esc(s.termMonths)} months` : ' · open-ended'}</div>
        </td>
        <td class="right strong">${esc(money(s.monthlyAmountCents))}/mo</td>
      </tr>`).join('')
    : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>RubyTill invoice — ${esc(input.tenantName)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
         color: #1c1917; margin: 0; padding: 32px; background: #f5f5f4; }
  .sheet { max-width: 820px; margin: 0 auto; background: #fff; border: 1px solid #e7e5e4;
           border-radius: 14px; padding: 36px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 2px solid #e11d48; padding-bottom: 18px; margin-bottom: 24px; }
  .brand { font-size: 22px; font-weight: 800; letter-spacing: -0.4px; }
  .brand span { color: #e11d48; }
  h2 { font-size: 11px; text-transform: uppercase; letter-spacing: .12em;
       color: #a8a29e; margin: 26px 0 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .1em;
       color: #a8a29e; padding: 0 0 8px; border-bottom: 1px solid #e7e5e4; }
  td { padding: 10px 0; border-bottom: 1px solid #f5f5f4; vertical-align: top; }
  .right { text-align: right; }
  .strong { font-weight: 700; }
  .muted { color: #a8a29e; font-size: 11px; }
  .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  ul { margin: 0; padding-left: 18px; font-size: 13px; }
  li { padding: 3px 0; }
  .pill { display: inline-block; font-size: 10px; font-weight: 800; text-transform: uppercase;
          letter-spacing: .06em; padding: 3px 8px; border-radius: 999px; }
  .pill.ok { background: #d1fae5; color: #065f46; }
  .pill.warn { background: #fef3c7; color: #92400e; }
  .totals { margin-top: 26px; border-top: 2px solid #1c1917; padding-top: 14px; }
  .totals .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 14px; }
  .totals .row.big { font-size: 18px; font-weight: 800; }
  .ids { margin-top: 26px; font-size: 11px; color: #78716c; }
  .ids a { color: #e11d48; }
  .bar { max-width: 820px; margin: 0 auto 16px; display: flex; gap: 10px; justify-content: flex-end; }
  .bar button { font: inherit; font-weight: 700; font-size: 13px; padding: 10px 18px;
                border-radius: 10px; border: 0; background: #e11d48; color: #fff; cursor: pointer; }
  .bar .ghost { background: #fff; color: #44403c; border: 1px solid #d6d3d1; }
  @media print {
    body { background: #fff; padding: 0; }
    .sheet { border: 0; border-radius: 0; padding: 0; max-width: none; }
    .bar { display: none !important; }
    @page { margin: 16mm; }
  }
</style>
</head>
<body>
  <div class="bar">
    <button class="ghost" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print / Save as PDF</button>
  </div>

  <div class="sheet">
    <div class="head">
      <div>
        <div class="brand">Ruby<span>Till</span></div>
        <div class="muted" style="margin-top:6px">Invoice summary</div>
      </div>
      <div class="right">
        <div class="strong">${esc(input.tenantName)}</div>
        <div class="muted mono">${esc(input.tenantId)}</div>
        ${input.orderId ? `<div class="muted mono">${esc(input.orderId)}</div>` : ''}
        <div class="muted" style="margin-top:6px">${esc(new Date().toLocaleDateString())}</div>
      </div>
    </div>

    <h2>Charged</h2>
    <table>
      <thead><tr><th>What was charged</th><th>Date</th><th>Status</th><th class="right">Amount</th></tr></thead>
      <tbody>${chargeRows}</tbody>
    </table>

    <h2>What the monthly subscription buys</h2>
    <ul>${packageRows}</ul>

    ${scheduleRows ? `
    <h2>Hardware billed monthly</h2>
    <table><tbody>${scheduleRows}</tbody></table>` : ''}

    <div class="totals">
      <div class="row"><span>Paid to date</span><span class="strong">${esc(money(paidCents))}</span></div>
      ${typeof input.softwareMonthlyCents === 'number' ? `
      <div class="row"><span class="muted" style="font-size:12px">Software, monthly${
        input.packageNames.length ? ` · ${esc(input.packageNames.length)} package${input.packageNames.length === 1 ? '' : 's'}` : ''
      }</span><span class="muted" style="font-size:12px">${esc(money(input.softwareMonthlyCents))}</span></div>` : ''}
      ${typeof input.hardwareMonthlyCents === 'number' && input.hardwareMonthlyCents > 0 ? `
      <div class="row"><span class="muted" style="font-size:12px">Hardware rental, monthly${
        input.schedule.length ? ` · ${esc(input.schedule.length)} line${input.schedule.length === 1 ? '' : 's'}` : ''
      }</span><span class="muted" style="font-size:12px">${esc(money(input.hardwareMonthlyCents))}</span></div>` : ''}
      <div class="row"><span>Subscription, monthly</span><span class="strong">${esc(money(input.monthlyCents))}</span></div>
      <div class="row big"><span>Total charged to date</span><span>${esc(money(paidCents))}</span></div>
    </div>

    <div class="ids">
      <div>
        This is RubyTill's own summary. Stripe holds one managed line per location at its net
        amount and is deliberately told nothing about packages or discounts, so its invoice
        cannot itemise what is above.
      </div>
      ${input.stripeCustomerId ? `<div style="margin-top:8px">Customer:
        <a href="${esc(input.stripeDashboardBase)}/customers/${esc(input.stripeCustomerId)}" target="_blank" rel="noopener noreferrer">${esc(input.stripeCustomerId)}</a></div>` : ''}
      ${input.stripeSubscriptionId ? `<div>Subscription:
        <a href="${esc(input.stripeDashboardBase)}/subscriptions/${esc(input.stripeSubscriptionId)}" target="_blank" rel="noopener noreferrer">${esc(input.stripeSubscriptionId)}</a></div>` : ''}
    </div>
  </div>
</body>
</html>`;
}

/**
 * Opens the invoice in its own window. Returns false when the browser blocked
 * the popup, so the caller can say so rather than appearing to do nothing.
 */
export function openTenantInvoice(input: TenantInvoiceInput): boolean {
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) return false;
  win.document.open();
  win.document.write(buildTenantInvoiceHtml(input));
  win.document.close();
  win.focus();
  return true;
}
